export const qs = (sel, root = document) => root.querySelector(sel);
export const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

// Events known to benefit from `passive: true` - the browser can skip
// blocking the main thread waiting to see if `preventDefault()` is called,
// which directly improves INP/FID for any scroll/touch-heavy page.
const PASSIVE_BY_DEFAULT = new Set([
  'scroll', 'wheel', 'mousewheel',
  'touchstart', 'touchmove', 'touchend',
  'pointerenter', 'pointerleave', 'pointermove',
]);

function defaultOpts(event, opts) {
  if (!PASSIVE_BY_DEFAULT.has(event)) return opts;
  if (opts === false) return false;
  if (opts && typeof opts === 'object') {
    return 'passive' in opts ? opts : { ...opts, passive: true };
  }
  return { passive: true, capture: opts === true };
}

export function on(target, event, handler, opts) {
  if (!target) return () => { };
  const resolved = defaultOpts(event, opts);
  target.addEventListener(event, handler, resolved);
  return () => target.removeEventListener(event, handler, resolved);
}

// Event delegation: listens for `event` on `root`, runs `handler` only when
// e.target.closest(selector) matches.
export function delegate(root, event, selector, handler, opts) {
  if (!root) return () => { };
  const wrapped = (e) => {
    const match = e.target.closest(selector);
    if (match && root.contains(match)) handler(e, match);
  };
  const resolved = defaultOpts(event, opts);
  root.addEventListener(event, wrapped, resolved);
  return () => root.removeEventListener(event, wrapped, resolved);
}

export function ready(fn) {
  if (document.readyState !== 'loading') fn();
  else document.addEventListener('DOMContentLoaded', fn, { once: true });
}

export function lockScroll(lock) {
  document.documentElement.style.overflow = lock ? 'hidden' : '';
}

export function trapFocus(container, opts = {}) {
  const focusables = qsa(
    'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
    container
  );
  if (focusables.length === 0) return () => { };
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const handler = (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  container.addEventListener('keydown', handler);
  if (!opts.skipInitialFocus) first.focus();
  return () => container.removeEventListener('keydown', handler);
}

/**
 * Strip leading/trailing whitespace from a form's text fields, in place.
 *
 * Call this BEFORE checkValidity(). The HTML5 `required` constraint only tests
 * for the empty string, so a field holding a single Space counts as filled and
 * the form submits with a value that is, to a human, blank - reported by the
 * client on the homepage stock-alert band, where pressing Space satisfied the
 * required "Marca și modelul". Trimming also keeps stray spaces out of the
 * stored lead.
 *
 * Only typed text is touched: checkboxes, radios, files and selects carry
 * option values rather than user input, so they are left alone.
 */
export function trimFields(form) {
  if (!form) return;
  const TEXTUAL = ['text', 'tel', 'email', 'search', 'url', 'number'];
  qsa('input, textarea', form).forEach((field) => {
    if (typeof field.value !== 'string') return;
    const type = (field.type || 'text').toLowerCase();
    if (field.tagName !== 'TEXTAREA' && !TEXTUAL.includes(type)) return;
    const trimmed = field.value.trim();
    if (trimmed !== field.value) field.value = trimmed;
  });
}
